import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Icon, PageTree } from '../../../../components';
import { scoreTone } from '../../keyword-checks';
import { routeTree } from '../../route-tree';
import type { SiteSeoPage, SiteSeoRoute } from '../../seo-types';
import { AddRow, ChangedDot, ErrorMark, Marks, MissingMark, Root, ScoreMark } from './RouteList.styles';

export { pageLabel } from '../../route-tree';

export type RouteListProps = {
  /** The draft's routes. */
  routes: SiteSeoRoute[];
  /** The site's pages, in the config's order. */
  pages: SiteSeoPage[];
  /** The selected path (`*` for Site defaults). */
  selected: string;
  language: string;
  defaultLanguage: string;
  /** Paths with unpublished changes (`*` for Site defaults). */
  changed: Set<string>;
  /** Paths with text in the default language but not in `language`. */
  missingLanguage: Set<string>;
  /** Site pages no longer on the site (kept in the draft until the next publish prunes them). */
  orphans: Set<string>;
  /** The owner's routes the site no longer has a page for (the server's `page-missing` warning). */
  missingPages: Set<string>;
  /** Paths holding an error that blocks Publish (`*` for Site defaults), in any language. */
  errors: Set<string>;
  /** The keyword score of each route with its own focus keyword (no entry, no badge). */
  scores?: ReadonlyMap<string, { passed: number; total: number }>;
  onSelect: (path: string) => void;
  onAdd: () => void;
};

/**
 * The SEO page's route list, as the Game Lists page tree: a search, Site defaults, then the site's pages with the
 * routes under each one's path in its branch (dynamic pages, the owner's own pages and footer wildcards), the owner's
 * other routes after them, and Add route. Marks at the end of a row: a warning on a route the site no longer has
 * (a page it dropped, or an owner's route with no page), the red one on a route holding an error that blocks Publish,
 * a warning on a route missing the selected language, the keyword score of a route with its own focus keyword, and a
 * blue dot on a route with unpublished changes.
 */
export const RouteList = ({
  routes,
  pages,
  selected,
  language,
  defaultLanguage,
  changed,
  missingLanguage,
  orphans,
  missingPages,
  errors,
  scores,
  onSelect,
  onAdd,
}: RouteListProps): React.ReactElement => {
  const { t } = useTranslation();
  const nodes = routeTree(routes, pages, language, defaultLanguage, {
    siteDefaults: t('Site defaults'),
    homepage: t('Homepage'),
    everyPage: t('every page'),
    footerOnly: t('footer only'),
  });

  const score = (path: string): React.ReactElement | null => {
    const value = scores?.get(path);
    if (!value) return null;
    const tone = scoreTone(value.passed, value.total);
    return (
      <ScoreMark
        role="img"
        $tone={tone}
        data-score={tone}
        aria-label={t('Keyword score {{passed}} of {{total}}', { passed: value.passed, total: value.total })}
      >
        {`${value.passed}/${value.total}`}
      </ScoreMark>
    );
  };

  const marks = (path: string): React.ReactElement => (
    <Marks>
      {orphans.has(path) ? (
        <MissingMark role="img" aria-label={t('No longer on the site')}>
          <Icon name="warning" size={14} />
        </MissingMark>
      ) : null}
      {errors.has(path) ? (
        <ErrorMark role="img" aria-label={t('Has an error that blocks Publish')}>
          <Icon name="warning" size={14} />
        </ErrorMark>
      ) : null}
      {missingPages.has(path) ? (
        <MissingMark role="img" aria-label={t('No longer on your site; its meta is not published')}>
          <Icon name="warning" size={14} />
        </MissingMark>
      ) : null}
      {missingLanguage.has(path) && language !== defaultLanguage ? (
        <MissingMark role="img" aria-label={t('Not written in this language yet')}>
          <Icon name="warning" size={14} />
        </MissingMark>
      ) : null}
      {score(path)}
      {changed.has(path) ? <ChangedDot role="img" aria-label={t('Unpublished changes')} /> : null}
    </Marks>
  );

  return (
    <Root>
      <PageTree nodes={nodes} openPath={selected} onOpen={onSelect} trailing={marks} />
      <AddRow>
        <Button label={t('Add route')} variant="outline" icon="plus" fullWidth onClick={onAdd} />
      </AddRow>
    </Root>
  );
};

export default RouteList;
